import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IIdName } from '@nimbus-library/features/shared/models/interfaces';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';
import { InputSearchComponent } from '@nimbus-library/features/shared/components/material/input-search/input-search.component';
import { MatDropDownComponent } from '@nimbus-library/features/shared/components/material/mat-drop-down/mat-drop-down.component';

export class IInputDropDownOption<T> implements IIdName<T> {
  public id: T;
  public name: string;
  public disabled?: boolean;
  public disabledItemTooltip?: string;
  public warningItemTooltip?: string;
  public isBeta?: boolean;
  public isGroup?: boolean;
  public infoMessage?: string;
}

@Component({
  standalone: false,
  selector: 'nimbus-input-dropdown',
  templateUrl: './input-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputDropdownComponent),
    },
  ],
})
export class InputDropdownComponent<T> extends IInputComponent<T> implements OnChanges, AfterViewInit {
  @Input()
  public warningMessage: string;
  @Input()
  public disabledItemTooltipText: string;
  @Input()
  public withSearch: boolean = false;
  @Input()
  public panelClass: string;
  @Input()
  public tooltipProp: string = 'name';
  @Input()
  public floatLabel: 'auto' | 'always' = 'auto';

  @Output()
  public afterViewInit: EventEmitter<NgControl> = new EventEmitter<NgControl>();

  @ViewChild('searchComponent')
  public searchComponent: InputSearchComponent;
  @ViewChild('dropDownComponent', { static: true })
  public dropDownComponent: MatDropDownComponent;

  public _options: IInputDropDownOption<T>[] = [];
  public selectedValue: IInputDropDownOption<T>;
  public searchInput: UntypedFormControl = new UntypedFormControl();

  @Input()
  public get value(): T {
    return this.innerValue;
  }

  @Input()
  public get disabled(): boolean {
    return this.isDisabled;
  }

  @Input()
  public set options(value: IInputDropDownOption<T>[]) {
    this._options = value || [];
    // Re-sync and re-render even when no value is selected, otherwise the OnPush panel stays empty.
    this.onValueChanged(this.innerValue);
    this.markForCheck();
  }

  public get options(): IInputDropDownOption<T>[] {
    return this._options;
  }

  public set disabled(value: boolean) {
    this.isDisabled = value;
    this.stateChange.emit();
  }

  public set value(value: T) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.propagateChange(value);
    }
    this.onValueChanged(this.innerValue);
  }

  public constructor(changeDetectionRef: ChangeDetectorRef, injector: Injector, translateService: TranslateService) {
    super(changeDetectionRef, injector, translateService);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && !!changes.options.currentValue) {
      this.onValueChanged(this.value);
    }
  }

  public ngAfterViewInit(): void {
    this.afterViewInit.emit(this.ngControl);
  }

  public writeValue(value: T): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
    this.onValueChanged(this.innerValue);
    this.markForCheck();
  }

  public onValueChanged(value: any): void {
    if ((value || value === 0) && this.options) {
      this.selectedValue = this.options.find((item) => this.areSameOptionId(item.id, value));
    } else {
      this.selectedValue = null;
    }
  }

  public onPanelToggle(isOpened: boolean): void {
    if (isOpened && this.searchComponent) {
      this.searchComponent.focus();
    }
  }

  public closePanel(): void {
    this.dropDownComponent.close();
  }

  private areSameOptionId(optionId: T, value: T): boolean {
    if (optionId === value) {
      return true;
    }

    if (typeof optionId !== 'string' || typeof value !== 'string') {
      return false;
    }

    const normalizedOptionId = this.normalizeIdentifier(optionId);
    const normalizedValue = this.normalizeIdentifier(value);
    return normalizedOptionId === normalizedValue;
  }

  private normalizeIdentifier(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }
}
